<template>
  <transition name="sab-fade">
    <div v-if="active" class="sab-banner" role="alert" aria-live="polite">
      <span v-if="_isSvgIconWrap(iconClass)" class="sab-icon ni-svg" v-html="_svgIconHtmlWrap(iconClass)" aria-hidden="true"></span>
      <i v-else class="fa sab-icon" :class="iconClass" aria-hidden="true"></i>
      <!-- tap-to-open target: body + desktop "Read more" button share a
           single click target so mobile doesn't need a separate button
           that would consume another row of vertical space. -->
      <button type="button" class="sab-open" @click="openDetail">
        <span class="sab-body">
          <span class="sab-title">{{ active.title }}</span>
          <span class="sab-preview" v-if="previewText">{{ previewText }}</span>
        </span>
        <span class="sab-read-label">
          Read more <i class="fa fa-angle-right" aria-hidden="true"></i>
        </span>
        <i class="fa fa-angle-right sab-read-chevron" aria-hidden="true"></i>
      </button>
      <button type="button" class="sab-close-btn" @click.stop="dismiss" aria-label="Dismiss">
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'
import { record_notification_action as cm_record_notification_action } from '../networking/apis'
import { tbn_record_notification_action } from '../networking/tbn_apis'
import { iconForNotification, isSvgIcon, customSvgHtml } from '../utils/notification_icons'

// parse banner_expires_at and compare to a passed-in 'now' so the
// computed runs O(1) per notification. unparseable values fall through
// as 'no expiry' to fail-open — the banner keeps showing if the field
// is malformed rather than silently disappearing.
function _bannerExpiresInFuture(iso, nowMs) {
  if (!iso) return true
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return true
  return t > nowMs
}

// site-wide banner for the most-recent unread service_alert that the admin
// marked as `banner: true` at creation time. read more + close both mark the
// notification as read so the banner goes away — close is effectively "I've
// seen it, don't keep nagging me".
export default {
  name: 'ServiceAlertBanner',
  computed: {
    ...mapState({
      user: state => state.profile.user,
      notifications: state => state.notifications.notifications,
    }),
    isTbn() {
      const tenant = this.user && this.user.tenant
      const types = this.user && this.user.types
      return !!(tenant && types && types[tenant] === 'TBN')
    },
    // resolves the banner's puck glyph. prefers the per-row icon
    // override (rare on banners — most outages stay on the warning
    // triangle, but admins can pick wrench/cloud/plug/etc for clarity)
    // and falls back to the service-alert default.
    iconClass() {
      return iconForNotification(this.active || { type: 'service_alert' })
    },
    active() {
      const list = Array.isArray(this.notifications) ? this.notifications : []
      // most-recent first: list arrives sorted desc from the API. pick the
      // first unread service_alert carrying the banner flag whose expiry
      // (if set) is still in the future. banner_expires_at is denormalized
      // onto the user row at fan-out time, and updated in-place by
      // expire_announcement_banner so the banner clears for everyone on
      // the next list-poll tick (~120s) without touching read_at /
      // actioned_at — the notification stays in the dropdown.
      const nowMs = Date.now()
      return list.find(n => n
        && n.type === 'service_alert'
        && n.banner === true
        && !n.read_at
        && (!n.banner_expires_at || _bannerExpiresInFuture(n.banner_expires_at, nowMs))
      ) || null
    },
    // defensive strip for body_preview. brand-new rows are clean plain
    // text, but rows fanned out before the server's _strip_markdown was
    // fixed to handle embedded html can carry broken fragments like
    // `<iLabyrinth</i pit rock hands` (the old stripper ate `>` from
    // markdown blockquote prefixes but left `<`, leaving half-tags
    // visible). strip any `<…>` and any dangling `<word` / `</word`
    // fragment that has no closing `>` so the banner reads cleanly
    // regardless of when the row was written.
    previewText() {
      const raw = this.active && this.active.body_preview
      if (!raw) return ''
      return String(raw)
        .replace(/<[^>]*>/g, '')
        .replace(/<\/?[a-zA-Z][^\s<]*/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    },
  },
  methods: {
    _isSvgIconWrap(name) { return isSvgIcon(name) },
    _svgIconHtmlWrap(name) { return customSvgHtml(name) },
    async _persistAction(nid) {
      try {
        const fn = this.isTbn ? tbn_record_notification_action : cm_record_notification_action
        await fn(this.user.tenant, nid)
      } catch (_) { /* best-effort — local mark already applied */ }
    },
    openDetail() {
      if (!this.active) return
      const n = this.active
      const nid = n.notification_id
      this.$store.commit('notifications/apply_action_local', nid)
      this._persistAction(nid)
      const dest = (n.link || '').trim()
      if (dest && dest !== '/') {
        if (dest.startsWith('http')) window.location.href = dest
        else this.$router.push(dest).catch(() => {})
      }
    },
    dismiss() {
      if (!this.active) return
      const nid = this.active.notification_id
      this.$store.commit('notifications/apply_action_local', nid)
      this._persistAction(nid)
    },
  },
}
</script>

<style scoped>
/* amber palette matches the service-alert puck + chip (see notifications_list.vue).
   banner sits at the very top of the authenticated shell, above the router-view. */
.sab-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: linear-gradient(180deg, #fef3d5 0%, #fce9b9 100%);
  border-bottom: 1px solid #f0a957;
  color: #7a4e00;
  font-size: 13px;
  text-align: left;
}

/* icon puck — matches the elevated .announcement-puck-service-alert style
   on desktop (48px, soft amber border, amber drop-shadow) and the compact
   .nl-item-puck from the notifications list on mobile (28px, darker amber
   border, flat). keeps the banner visually aligned with the rest of the
   notification surfaces in FI-1432. */
.sab-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 2px solid #fde68a;
  background: #fef3c7;
  color: #d97706;
  font-size: 20px;
  line-height: 1;
  box-sizing: border-box;
  box-shadow:
    0 2px 5px rgba(217, 119, 6, 0.16),
    0 1px 2px rgba(217, 119, 6, 0.08);
}

/* the open button wraps the text block AND (on desktop) the Read-more
   label. it's a <button> for accessibility but styled like a clickable
   row so the whole title/preview area is tappable on mobile. */
.sab-open {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.sab-open:hover .sab-title,
.sab-open:focus-visible .sab-title { text-decoration: underline; }
.sab-open:focus { outline: none; }
.sab-open:focus-visible { outline: 2px solid #b8761f; outline-offset: 2px; border-radius: 4px; }

.sab-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sab-title {
  font-size: 16px;
  font-weight: 700;
  color: #6b3f00;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sab-preview {
  color: #7a4e00;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.3;
}

/* desktop "Read more" pill. hidden on mobile — the whole button is
   tap-to-open there, and a small chevron still hints at that. */
.sab-read-label {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid #f0a957;
  color: #6b3f00;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  transition: background 0.12s;
}
.sab-open:hover .sab-read-label { background: #fff; }

/* mobile-only chevron affordance — sits inside .sab-open, hidden on
   desktop (where the Read-more label plays that role). */
.sab-read-chevron {
  display: none;
  flex: 0 0 auto;
  color: #8a5a00;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
}

.sab-close-btn {
  background: transparent;
  border: 0;
  color: #8a5a00;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  transition: background 0.12s;
}

.sab-close-btn:hover { background: rgba(240, 169, 87, 0.2); }

.sab-fade-enter-active, .sab-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.sab-fade-enter, .sab-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* mobile: collapse to a single compact row. title + preview stack
   inside .sab-body (they already do on desktop too); the Read-more pill
   is replaced by a small chevron so everything fits on one vertical
   strip — icon, text, chevron, close. two lines tall max. */
@media (max-width: 640px) {
  .sab-banner { padding: 8px 12px; gap: 8px; }
  .sab-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border-width: 1.5px;
    border-color: #d97706;
    font-size: 14px;
    box-shadow: none;
  }
  .sab-open { gap: 6px; }
  .sab-title { font-size: 13px; }
  .sab-preview { font-size: 12px; }
  .sab-read-label { display: none; }
  .sab-read-chevron { display: inline-flex; align-items: center; }
  .sab-close-btn { width: 26px; height: 26px; }
}

/* very narrow (smaller than iPhone SE / Galaxy S8, or landscape keyboard-
   shrunken portrait): drop the preview so the banner stays one line.
   the title alone conveys the alert; tap to see details. */
@media (max-width: 340px) {
  .sab-preview { display: none; }
}
</style>
